/**
 * Serviço para gerenciar execuções no Sandbox
 */

import { getToken } from "./tokenService";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface TestStep {
  stepNumber: number;
  stepName: string;
  prompt: string;
  input: string;
  output: string | null;
  status: "success" | "error";
  duration: number;
  inputTokens: number;
  outputTokens: number;
  error?: {
    code: string;
    message: string;
  };
  fileData?: {
    buffer: ArrayBuffer;
    mimetype: string;
    originalname: string;
    size: number;
  };
}

export interface TestResult {
  id: string;
  productId: string;
  input: string;
  output: string;
  timestamp: string;
  duration: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  steps: TestStep[];
  executedBy?: string;
}

export interface ExecuteSandboxTestRequest {
  productId: string;
  input: string;
  files?: File[];
}

/**
 * Executa um teste de produto no Sandbox
 * @param {ExecuteSandboxTestRequest} request - Dados do teste
 * @returns {Promise<ApiResponse<TestResult>>} Resultado do teste
 */
export async function executeSandboxTest(
  request: ExecuteSandboxTestRequest
): Promise<ApiResponse<TestResult>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  try {
    const formData = new FormData();
    formData.append("productId", request.productId);
    formData.append("input", request.input);

    // Adicionar arquivos se existirem
    if (request.files && request.files.length > 0) {
      request.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    const response = await fetch(`${API_BASE_URL}/sandbox/execute`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // NÃO adicionar Content-Type - o browser define automaticamente com boundary
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error: any) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message: error.message || "Erro de conexão com a API",
        details: error.toString(),
      },
    };
  }
}
